'use client';

import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectActiveMarker,
  selectSelectedProvider,
  selectSelectedProviders,
  selectZoom,
  selectCenter,
  selectBounds,
  selectSelectedCities,
  selectSelectedStates,
  selectSelectedSpecialties,
  selectSelectedStatuses,
  selectSelectedProviderLists,
  selectMetricToggles,
  selectShowHeatmap,
  selectSelectedHeatmapMetric,
  setActiveMarker,
  setSelectedProvider,
  setSelectedProviders,
  setSearch,
  setSortValues,
  setCurrentPage,
  setZoom,
  setCenter,
  setBounds,
  setSelectedCities,
  setSelectedStates,
  setSelectedSpecialties,
  setSelectedStatuses,
  setSelectedProviderLists,
  setMetricToggle,
  setShowHeatmap,
  setSelectedHeatmapMetric,
  resetProfileStrategy,
  SelectedHeatmapMetric,
} from 'src/redux/slices/profileStrategySlice';

interface UseProfileStrategiesReturn {
  activeMarker: string | null;
  selectedProvider: any | null;
  selectedProviders: MedScout.Opportunity[] | null;
  search: string;
  sortValues: {
    sort: string;
    order: 'asc' | 'desc';
    sort_metric: string | null;
  };
  currentPage: number;
  setSearch: (search: string) => void;
  setSortValues: (values: {
    sort: string;
    order: 'asc' | 'desc';
    sort_metric: string | null;
  }) => void;
  setCurrentPage: (page: number) => void;
  zoom: number;
  center: {
    lat: number;
    lng: number;
  };
  bounds: {
    min_lat: number;
    max_lat: number;
    min_lng: number;
    max_lng: number;
  };
  selectedCities: string[];
  selectedStates: string[];
  selectedSpecialties: string[];
  selectedStatuses: string[];
  selectedProviderLists: string[];
  metricToggles: Record<string, string>;
  setActiveMarker: (markerId: string | null) => void;
  setSelectedProvider: (provider: any | null) => void;
  setSelectedProviders: (providers: MedScout.Opportunity[] | null) => void;
  clearSelectedProviders: () => void;
  setZoom: (zoom: number) => void;
  setCenter: (center: { lat: number; lng: number }) => void;
  setBounds: (bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }) => void;
  setSelectedCities: (cities: string[]) => void;
  setSelectedStates: (states: string[]) => void;
  setSelectedSpecialties: (specialties: string[]) => void;
  setSelectedStatuses: (statuses: string[]) => void;
  setSelectedProviderLists: (providerLists: string[]) => void;
  clearSelectedCities: () => void;
  clearSelectedStates: () => void;
  clearSelectedSpecialties: () => void;
  clearSelectedStatuses: () => void;
  clearSelectedProviderLists: () => void;
  setMetricToggle: (metricId: string, option: string) => void;
  showHeatmap: boolean;
  setShowHeatmap: (show: boolean) => void;
  selectedHeatmapMetric: SelectedHeatmapMetric | null;
  setSelectedHeatmapMetric: (metric: SelectedHeatmapMetric | null) => void;
  resetProfileStrategy: () => void;
}

/**
 * Hook for managing profile strategy state (filters, zoom, bounds, etc.)
 * Used in profile strategy pages at /profiles/{type}/{id}/strategy/{strategyId}
 */
const useProfileStrategies = (): UseProfileStrategiesReturn => {
  const dispatch = useDispatch();

  // Selectors
  const activeMarker = useSelector(selectActiveMarker);
  const selectedProvider = useSelector(selectSelectedProvider);
  const selectedProviders = useSelector(selectSelectedProviders);
  const search = useSelector((state: any) => state.profileStrategies.search);
  const sortValues = useSelector(
    (state: any) => state.profileStrategies.sortValues
  );
  const currentPage = useSelector(
    (state: any) => state.profileStrategies.currentPage
  );
  const zoom = useSelector(selectZoom);
  const center = useSelector(selectCenter);
  const bounds = useSelector(selectBounds);
  const selectedCities = useSelector(selectSelectedCities);
  const selectedStates = useSelector(selectSelectedStates);
  const selectedSpecialties = useSelector(selectSelectedSpecialties);
  const selectedStatuses = useSelector(selectSelectedStatuses);
  const selectedProviderLists = useSelector(selectSelectedProviderLists);
  const metricToggles = useSelector(selectMetricToggles);
  const showHeatmap = useSelector(selectShowHeatmap);
  const selectedHeatmapMetric = useSelector(selectSelectedHeatmapMetric);

  // Action creators
  const setActiveMarkerAction = useCallback(
    (markerId: string | null) => {
      dispatch(setActiveMarker(markerId));
    },
    [dispatch]
  );

  const setSelectedProviderAction = useCallback(
    (provider: any | null) => {
      dispatch(setSelectedProvider(provider));
    },
    [dispatch]
  );

  const setSelectedProvidersAction = useCallback(
    (providers: MedScout.Opportunity[] | null) => {
      dispatch(setSelectedProviders(providers));
    },
    [dispatch]
  );

  const clearSelectedProvidersAction = useCallback(() => {
    dispatch(setSelectedProviders(null));
  }, [dispatch]);

  const setZoomAction = useCallback(
    (zoom: number) => {
      dispatch(setZoom(zoom));
    },
    [dispatch]
  );

  const setCenterAction = useCallback(
    (center: { lat: number; lng: number }) => {
      dispatch(setCenter(center));
    },
    [dispatch]
  );

  const setBoundsAction = useCallback(
    (bounds: { north: number; south: number; east: number; west: number }) => {
      dispatch(setBounds(bounds));
    },
    [dispatch]
  );

  const setSelectedCitiesAction = useCallback(
    (cities: string[]) => {
      dispatch(setSelectedCities(cities));
    },
    [dispatch]
  );

  const setSelectedStatesAction = useCallback(
    (states: string[]) => {
      dispatch(setSelectedStates(states));
    },
    [dispatch]
  );

  const setSelectedSpecialtiesAction = useCallback(
    (specialties: string[]) => {
      dispatch(setSelectedSpecialties(specialties));
    },
    [dispatch]
  );

  const setSelectedStatusesAction = useCallback(
    (statuses: string[]) => {
      dispatch(setSelectedStatuses(statuses));
    },
    [dispatch]
  );

  const setSelectedProviderListsAction = useCallback(
    (providerLists: string[]) => {
      dispatch(setSelectedProviderLists(providerLists));
    },
    [dispatch]
  );

  const clearSelectedCitiesAction = useCallback(() => {
    dispatch(setSelectedCities([]));
  }, [dispatch]);

  const clearSelectedStatesAction = useCallback(() => {
    dispatch(setSelectedStates([]));
  }, [dispatch]);

  const clearSelectedSpecialtiesAction = useCallback(() => {
    dispatch(setSelectedSpecialties([]));
  }, [dispatch]);

  const clearSelectedStatusesAction = useCallback(() => {
    dispatch(setSelectedStatuses([]));
  }, [dispatch]);

  const clearSelectedProviderListsAction = useCallback(() => {
    dispatch(setSelectedProviderLists([]));
  }, [dispatch]);

  const setMetricToggleAction = useCallback(
    (metricId: string, option: string) => {
      dispatch(setMetricToggle({ metricId, option }));
    },
    [dispatch]
  );

  const setShowHeatmapAction = useCallback(
    (show: boolean) => {
      dispatch(setShowHeatmap(show));
    },
    [dispatch]
  );

  const setSelectedHeatmapMetricAction = useCallback(
    (metric: SelectedHeatmapMetric | null) => {
      dispatch(setSelectedHeatmapMetric(metric));
    },
    [dispatch]
  );

  const resetProfileStrategyAction = useCallback(() => {
    dispatch(resetProfileStrategy());
  }, [dispatch]);

  const setSearchAction = useCallback(
    (search: string) => {
      dispatch(setSearch(search));
    },
    [dispatch]
  );

  const setSortValuesAction = useCallback(
    (values: {
      sort: string;
      order: 'asc' | 'desc';
      sort_metric: string | null;
    }) => {
      dispatch(setSortValues(values));
    },
    [dispatch]
  );

  const setCurrentPageAction = useCallback(
    (page: number) => {
      dispatch(setCurrentPage(page));
    },
    [dispatch]
  );

  return {
    activeMarker,
    selectedProvider,
    selectedProviders,
    search,
    sortValues,
    currentPage,
    zoom,
    center,
    bounds,
    selectedCities,
    selectedStates,
    selectedSpecialties,
    selectedStatuses,
    selectedProviderLists,
    metricToggles,
    setActiveMarker: setActiveMarkerAction,
    setSelectedProvider: setSelectedProviderAction,
    setSelectedProviders: setSelectedProvidersAction,
    clearSelectedProviders: clearSelectedProvidersAction,
    setSearch: setSearchAction,
    setSortValues: setSortValuesAction,
    setCurrentPage: setCurrentPageAction,
    setZoom: setZoomAction,
    setCenter: setCenterAction,
    setBounds: setBoundsAction,
    setSelectedCities: setSelectedCitiesAction,
    setSelectedStates: setSelectedStatesAction,
    setSelectedSpecialties: setSelectedSpecialtiesAction,
    setSelectedStatuses: setSelectedStatusesAction,
    setSelectedProviderLists: setSelectedProviderListsAction,
    clearSelectedCities: clearSelectedCitiesAction,
    clearSelectedStates: clearSelectedStatesAction,
    clearSelectedSpecialties: clearSelectedSpecialtiesAction,
    clearSelectedStatuses: clearSelectedStatusesAction,
    clearSelectedProviderLists: clearSelectedProviderListsAction,
    setMetricToggle: setMetricToggleAction,
    showHeatmap,
    setShowHeatmap: setShowHeatmapAction,
    selectedHeatmapMetric,
    setSelectedHeatmapMetric: setSelectedHeatmapMetricAction,
    resetProfileStrategy: resetProfileStrategyAction,
  };
};

export default useProfileStrategies;
